<template>
  <!-- Nada de v-if aqui: sem cor a barra some sozinha, porque a classe sai vazia e a altura
       zera. Assim quem usa não precisa repetir a pergunta. -->
  <div class="ontos-accent"
       :class="[accentClass(color), (vertical || row) ? 'ontos-accent--vertical' : '', row ? 'ontos-accent--row' : '']"></div>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import {accentClass} from '@/utils/colors'

/**
 * A barra fina que diz "isto é desta cor".
 *
 * Nasceu no topo da raia do Kanban e virou a marca de um ITEM em toda tela: o topo do card, o
 * cabeçalho da ficha, a linha da listagem e das tabelas de relacionamento, o valor no meio do
 * texto, a opção e o valor escolhido num campo. Meia dúzia de lugares desenhando a mesma barra
 * — e é assim que uma cópia começa a divergir: basta alguém ajustar o tom num deles.
 *
 * Três modos, que são três papéis: **deitada** encima uma superfície, **de pé** acompanha um
 * bloco de texto, e **de linha** cola na borda esquerda de uma célula de tabela.
 *
 * O que varia de escala — espessura, altura e raio — entra por VARIÁVEL CSS, e nunca por classe
 * de quem usa: duas regras disputando a mesma propriedade no mesmo elemento se resolvem pela
 * ordem no bundle, e foi assim que a régua do cabeçalho nasceu invisível uma vez.
 *
 * O tom é o `darken-1` do `accentClass`, o mesmo que a miniatura do menu de raias usa. A cor
 * nunca é escrita aqui: chega pronta de quem sabe de quem ela é.
 */
@Component({name: 'ontos-color-accent'})
class ColorAccent extends Vue {
  @Prop({type: String, default: ''}) readonly color!: string
  /**
   * De pé, ao lado do que ela marca, em vez de deitada por cima.
   *
   * A deitada encima uma superfície — é a raia do Kanban. A de pé acompanha um bloco de texto e
   * é a que se enxerga num cabeçalho: 3px na horizontal, acima de um título, passam
   * despercebidos; ao lado dele, na altura dele, não.
   */
  @Prop({type: Boolean, default: false}) readonly vertical!: boolean
  /**
   * De pé e COLADA na borda esquerda da caixa que a hospeda — a linha de uma tabela.
   *
   * Implica `vertical`. Existe como modo, e não como CSS de cada tela, porque a receita tem
   * três partes que precisam andar juntas e já estavam escritas duas vezes: absoluta, presa em
   * cima e embaixo (altura em porcentagem dentro de célula de tabela depende de o navegador ter
   * resolvido a altura da linha) e com folga vertical, senão as réguas de linhas seguidas se
   * emendam numa listra só.
   *
   * A única coisa que fica com quem usa é o `position: relative` da célula, que é do pai.
   */
  @Prop({type: Boolean, default: false}) readonly row!: boolean

  //Exposta ao template: função de módulo não é enxergada de lá.
  accentClass = accentClass
}

export default toNative(ColorAccent)
</script>

<style scoped>
/* O raio acompanha o canto de cima da superfície que ela encima — card da raia, card do
   modal, folha da página. Altura fixa: é um acento, não um bloco. */
.ontos-accent {
  height: 3px;
  flex: 0 0 auto;
  border-radius: var(--ontos-accent-radius, 4px 4px 0 0);
}

/* De pé: a largura passa a ser a espessura, e a altura vem de quem a hospeda — o
   `align-self: stretch` faz a régua acompanhar o bloco que ela marca.

   A altura também aceita variável, e por um motivo próprio: esticar só funciona quando há
   irmão dando altura à linha. Filha única de um flex — como no item de uma lista suspensa —, o
   pai mede pelo filho e o filho pelo pai, e a régua some. Ali quem usa diz de quanto ela é. */
.ontos-accent--vertical {
  height: var(--ontos-accent-height, auto);
  width: var(--ontos-accent-width, 4px);
  align-self: stretch;
  border-radius: var(--ontos-accent-radius, 4px);
}

/* De linha: presa em cima e embaixo, e não em altura relativa — dentro de uma célula de tabela,
   porcentagem depende de o navegador ter resolvido a altura da linha.

   A folga entra por variável porque o papel dela muda: entre linhas de uma tabela ela é o que
   impede as réguas de se emendarem numa listra só; encostada na borda de um card, ela não deve
   existir — ali a régua É a borda. */
.ontos-accent--row {
  position: absolute;
  left: 0;
  top: var(--ontos-accent-inset, 4px);
  bottom: var(--ontos-accent-inset, 4px);
}
</style>
